import { useEffect, useRef, useState } from 'react';
import { Pause, Play, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import { StillnessAudio, SoundTheme } from './stillnessAudio';
import './stillness-audio.css';

export default function Stillness() {
  const [remaining, setRemaining] = useState(30);
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [audioUnavailable, setAudioUnavailable] = useState(false);
  const [theme, setTheme] = useState<SoundTheme>(() => typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light');
  const audio = useRef<StillnessAudio | null>(null);
  const stopAudio = () => { audio.current?.stop(); audio.current = null; };

  useEffect(() => {
    const sync = () => {
      const next = document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
      setTheme(next); audio.current?.setTheme(next);
    };
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    sync();return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setRemaining(n => Math.max(0, n - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [running]);
  useEffect(() => { if (remaining === 0) { setRunning(false); stopAudio(); } }, [remaining]);
  useEffect(() => {
    const pauseWhenHidden = () => { if (document.hidden) { setRunning(false); stopAudio(); } };
    document.addEventListener('visibilitychange', pauseWhenHidden);
    return () => { document.removeEventListener('visibilitychange', pauseWhenHidden); stopAudio(); };
  }, []);

  const toggle = () => {
    if (running) { setRunning(false); stopAudio(); return; }
    if (remaining === 0) setRemaining(30);
    setRunning(true);setAudioUnavailable(false);
    try {
      const sound = new StillnessAudio();audio.current = sound;
      void sound.start(theme, muted).catch(() => { if (audio.current === sound) { setAudioUnavailable(true);stopAudio(); } });
    } catch { setAudioUnavailable(true); }
  };
  const phase = remaining === 0 ? 'Welcome back.' : remaining > 20 ? 'Simply arrive.' : remaining > 10 ? 'Let this moment be yours.' : 'There is no hurry.';
  return <section className={`stillness-section ${running ? 'is-running' : ''}`} aria-labelledby="stillness-title">
    <div className="container"><p className="observatory-label">A small interlude</p><h2 id="stillness-title">Before the next thing,<br/><em>just this moment.</em></h2><p>A little space in a busy day. Stay with the {theme === 'dark' ? 'quiet' : 'light'} for thirty seconds.</p>
      <div className="stillness-instrument"><i/><i/><i/><div><span aria-live="off">{String(remaining).padStart(2, '0')}</span><small>seconds of space</small></div></div>
      <p className="stillness-phase" role="status">{running || remaining === 0 ? phase : 'Whenever you’re ready.'}</p>
      <div className="stillness-controls"><button onClick={toggle}>{running ? <Pause size={17}/> : <Play size={17}/>} {running ? 'Pause' : remaining === 0 ? 'Begin again' : remaining < 30 ? 'Continue' : 'Take a moment'}</button>
        <button aria-label={muted ? 'Unmute soothing sound' : 'Mute soothing sound'} aria-pressed={muted} onClick={() => { const next = !muted;setMuted(next);audio.current?.setMuted(next); }}>{muted ? <VolumeX size={17}/> : <Volume2 size={17}/>}<span>{muted ? 'Sound off' : 'Sound on'}</span></button>
        {remaining < 30 && <button aria-label="Reset quiet moment" onClick={() => { setRemaining(30);setRunning(false);stopAudio(); }}><RotateCcw size={17}/></button>}
      </div>
      <div className={`stillness-soundtrack ${running && !muted && !audioUnavailable ? 'is-playing' : ''}`}><span className="soundtrack-bars" aria-hidden="true"><i/><i/><i/><i/><i/></span><span>{theme === 'light' ? 'Daybreak · airy chimes & soft harmonies' : 'Moonlit · deep tones & slow, warm bells'}</span></div>
      <p className="stillness-sound-hint" role="status">{audioUnavailable ? 'Sound couldn’t start on this device. Your quiet moment can continue in silence.' : 'Original ambient sound. Starts when you tap. Keep your volume comfortable.'}</p>
    </div>
  </section>;
}
